import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, Info, LogOut } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";

// Bilingual copy. The explanation MUST accurately distinguish source types:
// some data is observed / ADI-provided, some is illustrative, and all
// recommendations + campaign results are simulated.
export const DEMO_COPY = {
  fr: {
    badge: "Données de démonstration",
    explain:
      "Environnement de démonstration : certaines données sont observées ou fournies par ADI, d’autres sont illustratives. Les recommandations et résultats de campagne sont simulés.",
    readOnlyHint: "Indisponible dans l’espace de démonstration en lecture seule.",
    expiredTitle: "Accès de démonstration expiré",
    expiredBody:
      "Votre accès à l’espace de démonstration a expiré. Veuillez vous reconnecter ou contacter Lodge & Co.",
    backToLogin: "Retour à la connexion",
    logout: "Se déconnecter",
  },
  en: {
    badge: "Demonstration data",
    explain:
      "Demonstration environment: some data is observed or provided by ADI, other data is illustrative. Recommendations and campaign results are simulated.",
    readOnlyHint: "Unavailable in the read-only demonstration workspace.",
    expiredTitle: "Demonstration access expired",
    expiredBody:
      "Your access to the demonstration workspace has expired. Please sign in again or contact Lodge & Co.",
    backToLogin: "Back to sign in",
    logout: "Sign out",
  },
};

// Server-authoritative flags are surfaced by the backend on the org object.
// The frontend is only a usability layer — backend DEMO_READ_ONLY stays authoritative.
export function useDemo() {
  const { activeMembership, memberships, user } = useAuth();
  const { lang } = useLang();
  const org = activeMembership?.org || null;
  const isDemo = !!org?.is_demo;
  const isDemoImmutable = isDemo && org?.demo_mode === "immutable";
  const copy = DEMO_COPY[lang] || DEMO_COPY.fr;
  const singleMembership = (memberships?.length || 0) === 1 && !user?.is_platform_admin;
  return { org, isDemo, isDemoImmutable, readOnly: isDemoImmutable, copy, singleMembership };
}

// Persistent, accessible badge shown across the Demo journey (rendered in the TopBar).
export function DemoBadge({ className = "" }) {
  const { isDemoImmutable, copy } = useDemo();
  const [open, setOpen] = useState(false);
  if (!isDemoImmutable) return null;
  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        data-testid="demo-badge-topbar"
        aria-label={`${copy.badge}. ${copy.explain}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
        className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 sm:px-2.5"
      >
        <FlaskConical size={13} />
        {/* Full text label at >= sm; icon-only compact pill below sm to avoid
            TopBar overflow on narrow screens. Accessible name is preserved via
            aria-label + the always-present sr-only explanation. */}
        <span className="hidden sm:inline">{copy.badge}</span>
        <Info size={12} className="opacity-70" />
      </button>
      {open && (
        <div
          role="tooltip"
          data-testid="demo-badge-explain"
          className="absolute left-0 top-full z-40 mt-2 w-72 rounded-lg border border-amber-200 bg-white p-3 text-xs leading-relaxed text-slate-700 shadow-lg"
        >
          {copy.explain}
        </div>
      )}
      {/* Always available to assistive tech, even when the panel is collapsed. */}
      <span className="sr-only">{copy.explain}</span>
    </div>
  );
}

// Slim persistent notice on journey pages (rendered by AppLayout).
export function DemoReadOnlyBanner() {
  const { isDemoImmutable, copy } = useDemo();
  if (!isDemoImmutable) return null;
  return (
    <div
      data-testid="demo-readonly-banner"
      role="note"
      className="mb-6 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <FlaskConical size={16} className="mt-0.5 shrink-0 text-amber-600" />
      <p>
        <strong className="font-semibold">{copy.badge}.</strong> {copy.explain}
      </p>
    </div>
  );
}

// Inline hint shown next to a disabled control on a Demo-immutable org.
export function DemoHint({ className = "" }) {
  const { isDemoImmutable, copy } = useDemo();
  if (!isDemoImmutable) return null;
  return (
    <p data-testid="demo-readonly-hint" className={`mt-1 text-xs text-amber-700 ${className}`}>
      {copy.readOnlyHint}
    </p>
  );
}

// Safe access-expired state (no crash, no stale data, no internal details).
export function AccessExpired() {
  const { logout } = useAuth();
  const { copy } = useDemo();
  return (
    <div
      data-testid="access-expired"
      className="flex min-h-screen items-center justify-center bg-[#FAFAF6] px-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-[#E8E5DD] bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <FlaskConical size={22} />
        </div>
        <h1 className="text-lg font-semibold text-[#17201A]">{copy.expiredTitle}</h1>
        <p className="mt-2 text-sm text-slate-500">{copy.expiredBody}</p>
        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            data-testid="access-expired-logout"
            onClick={logout}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#1E3D2B] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:brightness-110"
          >
            <LogOut size={15} /> {copy.logout}
          </button>
          <Link
            to="/login"
            data-testid="access-expired-login"
            className="text-sm font-medium text-[#3E7256] hover:underline"
          >
            {copy.backToLogin}
          </Link>
        </div>
      </div>
    </div>
  );
}
